import React, { ReactNode } from 'react';
import { Accordion, AccordionSummary, AccordionDetails, Divider } from '@mui/material';
import colors from 'src/utils/colors';
import { Text } from 'src/components/BeaconV2Components/Generic/Text';
import Flex from 'src/components/BeaconV2Components/Flex/Flex';
import { CreatedBySection } from './CreatedBySection';
import { RightArrow, DownChevron } from 'src/components/SvgIcons/SvgIcons';

interface BaseRequirementCardProps {
  index: number;
  summaryText: string;
  title: string;
  requirementTypeLabel: string;
  children?: ReactNode; // Optional additional fields
  onEditClick: () => void;
}

export const BaseRequirementCard: React.FC<BaseRequirementCardProps> = ({
  index,
  summaryText,
  title,
  requirementTypeLabel,
  children,
  onEditClick
}) => {
  return (
    <Accordion
      defaultExpanded={index === 0}
      sx={{
        boxShadow: 'none',
        overflow: 'hidden',
        border: `1px solid ${colors.graysSecondary}`,
        borderRadius: '8px !important',
        padding: '16px',
        '&:before': {
          display: 'none'
        },
        '&.Mui-expanded': {
          margin: 0,
          paddingBottom: 0
        }
      }}
    >
      <AccordionSummary
        expandIcon={<DownChevron width={24} height={24} style={{ transition: 'transform 0.2s ease' }} />}
        sx={{
          minHeight: 'unset !important',
          padding: '0px',
          '& svg': {
            width: '24px',
            height: '24px',
            color: colors.darkBlue
          },
          '&.Mui-expanded': {
            minHeight: 'unset',
            margin: '0px',
            paddingBottom: '16px',
            '&:after': {
              content: '""',
              position: 'absolute',
              left: '-20px',
              bottom: '0px',
              width: '2000px',
              height: '1px',
              backgroundColor: colors.graysSecondary,
              zIndex: '1'
            }
          },
          '& .MuiAccordionSummary-content': {
            margin: '0px !important'
          }
        }}
      >
        <Flex gap={4} alignItems="center">
          <Text variant="body2" fontWeight={700} sx={{ whiteSpace: 'nowrap' }}>
            Requirement {index + 1}:
          </Text>
          <Text
            variant="body2"
            sx={{
              minWidth: 0,
              overflow: 'hidden',
              display: '-webkit-box',
              WebkitLineClamp: 1,
              WebkitBoxOrient: 'vertical',
              textOverflow: 'ellipsis'
            }}
          >
            {summaryText}
          </Text>
        </Flex>
      </AccordionSummary>
      <AccordionDetails sx={{ padding: '24px 0', height: '150px', boxSizing: 'border-box' }}>
        <Flex flexDirection="column" gap={24}>
          <Flex gap={64} alignItems="flex-start">
            <Flex flexDirection="column" gap={8}>
              <Text variant="body2" fontWeight={500}>
                Title
              </Text>
              <Text variant="body2" sx={{ lineHeight: '30px' }}>
                {title}
              </Text>
            </Flex>
            <Flex flexDirection="column" gap={8}>
              <Text variant="body2" fontWeight={500}>
                Requirement Type
              </Text>
              <Text variant="body2" sx={{ lineHeight: '30px' }}>
                {requirementTypeLabel}
              </Text>
            </Flex>
            {children}
            <CreatedBySection />
          </Flex>
          <div
            role="button"
            style={{
              width: 'fit-content',
              cursor: 'pointer'
            }}
            onClick={(e) => {
              e.stopPropagation();
              onEditClick();
            }}
          >
            <Text
              variant="body2"
              fontWeight={500}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                cursor: 'pointer',
                '&:hover': {
                  textDecoration: 'underline',
                  '& .hover-arrow': {
                    transform: 'translateX(2.5px)'
                  }
                }
              }}
            >
              Edit requirement
              <RightArrow
                style={{
                  height: '12px',
                  width: '12px',
                  transition: 'transform 0.2s ease-in-out'
                }}
                className="hover-arrow"
              />
            </Text>
          </div>
        </Flex>
      </AccordionDetails>
    </Accordion>
  );
};
